import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { ArrowLeft } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import { useSession } from '../../context/session-context';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { CountrySelect } from '../../components/ui/country-select';
import { AuthShell } from '../../components/auth/auth-shell';
import { trackRegistrationComplete, trackSignup } from '../../lib/analytics';
import {
  classifyEmailDomain,
  type RegistrationTracking,
} from '../../lib/registration-tracking';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { INDUSTRIES } from '../../constants/industries';
import { STAFF_BANDS } from '../../constants/staff-bands';
import { stripPhonePrefix } from '../../helpers/strip-phone-prefix';
import {
  DuplicateEmailError,
  RegisterOrganisationPayload,
  useRegisterOrganisation,
} from '../../hooks/use-registration';

const MIN_PASSWORD_LENGTH = 8;
const INPUT_CLASS =
  'h-11 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none';
const ACCOUNT_FIELDS = ['name', 'surname', 'email', 'password'] as const;

type FormData = {
  companyName: string;
  industry: string;
  website: string;
  phone: string | undefined;
  staffBand: string;
  name: string;
  surname: string;
  email: string;
  password: string;
};

export default function RegisterPage() {
  const { session } = useSession();
  const [step, setStep] = useState<1 | 2>(1);
  const [registration, setRegistration] = useState<RegistrationTracking | null>(
    null,
  );
  const submitRegistration = useRegisterOrganisation();
  const form = useForm<FormData>({ shouldUnregister: false });

  if (session) return <Navigate to="/organization" />;
  if (registration) return <CheckEmailView registration={registration} />;

  const goNext = async (event: React.FormEvent) => {
    event.preventDefault();
    const valid = await form.trigger([...ACCOUNT_FIELDS]);
    if (valid) setStep(2);
  };

  const handleSubmitError = (error: Error) => {
    if (!isDuplicateEmailError(error)) return;
    form.setError('email', { message: 'This email is already registered.' });
    setStep(1);
  };

  const submitAll = form.handleSubmit((data) => {
    submitRegistration.mutate(toPayload(data), {
      onSuccess: () => {
        // No raw email or other form fields are retained in tracking state.
        setRegistration({
          companySize: data.staffBand,
          emailDomainType: classifyEmailDomain(data.email),
        });
        try {
          // Existing Ads reporting is independent of the new GA event.
          trackSignup('Email', data.staffBand);
        } catch {
          // Analytics must never prevent the successful account screen.
        }
      },
      onError: handleSubmitError,
    });
  });

  const handleBack = () => {
    submitRegistration.reset();
    setStep(1);
  };

  const showGenericError =
    submitRegistration.error &&
    !isDuplicateEmailError(submitRegistration.error);

  return (
    <AuthShell>
      {step === 2 && <BackLink onClick={handleBack} />}
      <Header step={step} />
      <form onSubmit={step === 1 ? goNext : submitAll} className="space-y-6">
        <div className={step === 1 ? '' : 'hidden'}>
          <AccountFields form={form} />
        </div>
        <div className={step === 2 ? '' : 'hidden'}>
          <CompanyFields form={form} />
        </div>
        {showGenericError && (
          <p className="text-sm text-red-500 text-center">
            {submitRegistration.error?.message}
          </p>
        )}
        <PrimaryButton
          label={getButtonLabel(step, submitRegistration.isPending)}
          disabled={submitRegistration.isPending}
        />
      </form>
      <SignInLink />
    </AuthShell>
  );
}

function getButtonLabel(step: 1 | 2, isLoading: boolean): React.ReactNode {
  if (step === 1) return 'Next';
  if (isLoading) return <LoadingSpinner />;
  return 'Create Account';
}

function isDuplicateEmailError(error: Error) {
  return error instanceof DuplicateEmailError;
}

function toPayload(data: FormData): RegisterOrganisationPayload {
  return {
    companyName: data.companyName,
    industry: data.industry || undefined,
    website: data.website || undefined,
    phone: data.phone ? stripPhonePrefix(data.phone) : undefined,
    staffBand: data.staffBand,
    name: data.name,
    surname: data.surname,
    email: data.email,
    password: data.password,
  };
}

function AccountFields({ form }: { form: UseFormReturn<FormData> }) {
  const {
    register,
    formState: { errors },
  } = form;
  return (
    <FieldGrid>
      <FieldGroup label="First Name" error={errors.name?.message}>
        <Input
          {...register('name', { required: 'First name is required' })}
          className={INPUT_CLASS}
        />
      </FieldGroup>
      <FieldGroup label="Last Name" error={errors.surname?.message}>
        <Input
          {...register('surname', { required: 'Last name is required' })}
          className={INPUT_CLASS}
        />
      </FieldGroup>
      <FieldGroup label="Email" error={errors.email?.message}>
        <Input
          type="email"
          {...register('email', { required: 'Email is required' })}
          className={INPUT_CLASS}
        />
      </FieldGroup>
      <FieldGroup label="Password" error={errors.password?.message}>
        <Input
          type="password"
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: MIN_PASSWORD_LENGTH,
              message: `At least ${MIN_PASSWORD_LENGTH} characters`,
            },
          })}
          className={INPUT_CLASS}
        />
      </FieldGroup>
    </FieldGrid>
  );
}

function CompanyFields({ form }: { form: UseFormReturn<FormData> }) {
  const {
    register,
    control,
    formState: { errors },
  } = form;
  return (
    <div className="space-y-4">
      <p className="text-sm text-subheading">
        You can change any of this later in your settings.
      </p>
      <FieldGrid>
        <FieldGroup label="Company Name" error={errors.companyName?.message}>
          <Input
            {...register('companyName', {
              required: 'Company name is required',
            })}
            className={INPUT_CLASS}
          />
        </FieldGroup>
        <FieldGroup label="Industry">
          <Controller
            control={control}
            name="industry"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={INPUT_CLASS}>
                  <SelectValue placeholder="Select an industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FieldGroup>
        <FieldGroup label="Website">
          <Input
            placeholder="example.com"
            {...register('website')}
            className={INPUT_CLASS}
          />
        </FieldGroup>
        <FieldGroup label="Phone" error={errors.phone?.message}>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                defaultCountry="ZA"
                international
                countryCallingCodeEditable={false}
                value={field.value}
                onChange={field.onChange}
                placeholder="79 123 4567"
                inputComponent={Input}
                countrySelectComponent={CountrySelect}
                numberInputProps={{
                  className:
                    'h-11 bg-white border border-gray-300 rounded-md focus:border-accent-purple focus:ring-accent-purple focus:outline-none',
                  style: { fontSize: '14px' },
                }}
                className="flex gap-2 items-center"
              />
            )}
          />
        </FieldGroup>
        <FieldGroup label="How many staff?" error={errors.staffBand?.message}>
          <Controller
            control={control}
            name="staffBand"
            rules={{ required: 'Number of staff is required' }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  className={INPUT_CLASS}
                  aria-label="How many staff?"
                >
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent>
                  {STAFF_BANDS.map((band) => (
                    <SelectItem key={band} value={band}>
                      {band}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FieldGroup>
      </FieldGrid>
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-sm text-subheading hover:text-title transition-colors self-start"
    >
      <ArrowLeft className="h-4 w-4" />
      Back
    </button>
  );
}

function PrimaryButton({
  label,
  disabled,
}: {
  label: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <Button
      type="submit"
      disabled={disabled}
      className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
    >
      {label}
    </Button>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  );
}

function FieldGroup({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-title font-medium">{label}</Label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Header({ step }: { step: 1 | 2 }) {
  const isFirstStep = step === 1;
  return (
    <div className="text-center">
      <h1 className="text-2xl font-semibold tracking-tight">
        {isFirstStep ? 'Create an account' : 'Almost done'}
      </h1>
    </div>
  );
}

function SignInLink() {
  return (
    <div className="text-center text-sm">
      <span className="text-muted-foreground">Already have an account? </span>
      <Link
        to="/sign-in"
        className="text-accent-purple hover:text-accent-purple/80"
      >
        Sign in
      </Link>
    </div>
  );
}

function CheckEmailView({
  registration,
}: {
  registration: RegistrationTracking;
}) {
  useEffect(() => {
    try {
      trackRegistrationComplete(registration);
    } catch {
      // A blocked analytics library must not replace the successful account UI.
    }
  }, [registration]);

  return (
    <AuthShell>
      <div className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Check your email
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          We sent you a link to verify your email. Click it to activate your
          account.
        </p>
      </div>
    </AuthShell>
  );
}
